// @flow
import { SendOutlined } from "@mui/icons-material";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	FormHelperText,
	TextField,
	Typography
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { ErrorMessage, FormikProvider } from "formik";
import { Trans, useTranslation } from "react-i18next";
import { textFieldDefaults } from "../../../../helpers/theme/defaultProps";
import useForgotPassword from "../../../../hooks/login/useForgotPassword";
import useUserAction from "../../../../hooks/user/useUserAction";
import useUserData from "../../../../hooks/user/useUserData";
import { PAGE_LOGIN } from "../../../../locales/pages/namespaces";
import PasswordInput from "../../../mui/form/passwordInput/passwordInput.jsx";

type Props = {
	open: boolean,
	handleDialog: Function
};

const ForgotPassword = (props: Props) => {
	const { open, handleDialog } = props
	const { t } = useTranslation(PAGE_LOGIN)
	const { loading, passwordReset } = useUserData()
	const { setResetPassword } = useUserAction()
	const handleClose = (event, reason) => {
		if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) return
		formik.resetForm()
		setResetPassword(false)
		handleDialog(false)
	}
	const { formik } = useForgotPassword({ handleClose })
	
	return (
		<Dialog open={open} onClose={handleClose} maxWidth={'xs'}>
			<FormikProvider value={formik}>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					noValidate
					pt={1}
				>
					<DialogTitle>
						<Typography
							variant={'h4'}
							component={'div'}
						>
							{t('Password reset')}
						</Typography>
					</DialogTitle>
					<DialogContent>
						<DialogContentText>
							<Trans
								i18nKey={'reset_password'}
								ns={PAGE_LOGIN}
							>
								To reset your password, please enter your username here. <br/> You will receive a six
								digit code
								to your email. Use this code to set your new password.
							</Trans>
						</DialogContentText>
						<Box
							pt={1}
						>
							<FormControl
								variant="standard"
								fullWidth
								error={
									formik.touched['username'] &&
									Boolean(formik.errors['username'])
								}
							>
								<TextField
									{...textFieldDefaults}
									value={formik.values.username}
									name={'username'}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder={t('Enter your username')}
									size={'small'}
									slotProps={{
										htmlInput: {
											maxLength: 250
										},
									}}
									required
									disabled={passwordReset}
								/>
								<FormHelperText>
									<ErrorMessage name={'username'}/>
								</FormHelperText>
							</FormControl>
							{passwordReset && (
								<>
									<FormControl
										variant="standard"
										fullWidth
										error={
											formik.touched['sixDigitCode'] &&
											Boolean(formik.errors['sixDigitCode'])
										}
									>
										<TextField
											{...textFieldDefaults}
											value={formik.values.sixDigitCode}
											name={'sixDigitCode'}
											onChange={formik.handleChange}
											onBlur={formik.handleBlur}
											placeholder={t('Six digit code')}
											size={'small'}
											slotProps={{
												htmlInput: {
													maxLength: 250
												},
											}}
											required
										/>
										<FormHelperText>
											<ErrorMessage name={'sixDigitCode'}/>
										</FormHelperText>
									</FormControl>
									<FormControl
										variant="standard"
										fullWidth
										error={
											formik.touched['newPassword'] &&
											Boolean(formik.errors['newPassword'])
										}
									>
										<PasswordInput
											formik={formik}
											value={formik.values.password}
											onChange={formik.handleChange}
											onBlur={formik.handleBlur}
											name={'newPassword'}
											required
											otherProps={{
												size: 'small',
												placeholder: t('New password'),
												...textFieldDefaults
											}}
										/>
										<FormHelperText>
											<ErrorMessage name={'newPassword'}/>
										</FormHelperText>
									</FormControl>
									<FormControl
										variant="standard"
										fullWidth
										error={
											formik.touched['confirmPassword'] &&
											Boolean(formik.errors['confirmPassword'])
										}
									>
										<PasswordInput
											formik={formik}
											value={formik.values.password}
											onChange={formik.handleChange}
											onBlur={formik.handleBlur}
											name={'confirmPassword'}
											required
											otherProps={{
												size: 'small',
												placeholder: t('Confirm password'),
												...textFieldDefaults
											}}
										/>
										<FormHelperText>
											<ErrorMessage name={'confirmPassword'}/>
										</FormHelperText>
									</FormControl>
								</>
							)}
						</Box>
					</DialogContent>
					<DialogActions>
						<Button
							color="error"
							size={"small"}
							onClick={handleClose}
							sx={{
								marginRight: 'auto'
							}}
						>
							{t('Cancel')}
						</Button>
						<Button
							loading={loading}
							loadingPosition="start"
							size={"small"}
							variant={"contained"}
							color={'success'}
							type={'submit'}
							startIcon={<SendOutlined/>}
						>
							{t(passwordReset ? 'Submit New Password' : 'Send 6 Digit Code')}
						</Button>
					</DialogActions>
				</Box>
			</FormikProvider>
		</Dialog>
	);
};

export default ForgotPassword